import {
  LOOKUP_MASTERS,
  type CreateUserRequest,
  type UpdateUserRequest,
} from "api-client";
import {
  areSelectedUsersAllDeactivated,
  areSelectedUsersAllInviteSent,
  buildReactivateUsersDescription,
  buildResendInviteUsersDescription,
  ConfirmActionDialog,
  DeactivateUsersDialog,
  getThunkErrorMessage,
  ListFiltersDrawer,
  ListAppliedFilterChips,
  ListTablePaginationBar,
  type ListAppliedFilterChipItem,
  lookupItemsToFilterOptions,
  lookupItemsToRoleOptionStrings,
  USERS_STATUS_FILTER_OPTIONS,
  UserFormDrawer,
  type UserFormInitialValues,
  type UserFormSubmitPayload,
  UsersTable,
  UsersToolbar,
  useNotificationSnackbar,
  type UserRow,
  useLookup,
} from "design-components";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { lookupService } from "../../../shared/services/lookup.service";
import { SettingsPageShell } from "../components/settings-page-shell.component";
import { useAppDispatch, useAppSelector } from "../../../shared/store/hook";
import {
  clearSettingsUserSubmitState,
  clearSettingsUsersDeleteState,
  createSettingsUser,
  deleteSettingsUsers,
  fetchSettingsUsers,
  reactivateSettingsUsers,
  resendInviteSettingsUsers,
  SETTINGS_USERS_LIST_PAGE_SIZE,
  setUsersListFilters,
  setUsersListRange,
  setUsersListSearch,
  setUsersListSort,
  updateSettingsUser,
} from "../../../shared/store/settingsUsers";
import {
  selectSettingsUsersDeleteError,
  selectSettingsUsersDeleteSubmitting,
  selectSettingsUsersError,
  selectSettingsUsersFromIndex,
  selectSettingsUsersListFilterRoleName,
  selectSettingsUsersListFilterStatus,
  selectSettingsUsersLoading,
  selectSettingsUsersRows,
  selectSettingsUsersSortOrder,
  selectSettingsUsersSubmitError,
  selectSettingsUsersSubmitting,
  selectSettingsUsersTotal,
} from "../../../shared/store/settingsUsers/settings-users.selector";

/** Row id of the signed-in user for table self-action guards; replace when session provides it. */
const SETTINGS_CURRENT_USER_ID = "105";

type DrawerMode = "invite" | "edit";

export const SettingsUsersPage = () => {
  const dispatch = useAppDispatch();
  const rows = useAppSelector(selectSettingsUsersRows);
  const loading = useAppSelector(selectSettingsUsersLoading);
  const error = useAppSelector(selectSettingsUsersError);
  const sortingOrder = useAppSelector(selectSettingsUsersSortOrder);
  const submitting = useAppSelector(selectSettingsUsersSubmitting);
  const submitError = useAppSelector(selectSettingsUsersSubmitError);
  const deleteSubmitting = useAppSelector(selectSettingsUsersDeleteSubmitting);
  const deleteError = useAppSelector(selectSettingsUsersDeleteError);
  const appliedListFilterStatus = useAppSelector(selectSettingsUsersListFilterStatus);
  const appliedListFilterRoleName = useAppSelector(selectSettingsUsersListFilterRoleName);
  const totalUsers = useAppSelector(selectSettingsUsersTotal);
  const fromIndex = useAppSelector(selectSettingsUsersFromIndex);

  const [includeDeactivated, setIncludeDeactivated] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [deactivateOpen, setDeactivateOpen] = useState(false);
  const [reactivateConfirmOpen, setReactivateConfirmOpen] = useState(false);
  const [resendInviteConfirmOpen, setResendInviteConfirmOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState<DrawerMode>("invite");
  const [drawerInitial, setDrawerInitial] = useState<UserFormInitialValues | null>(null);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [filterDraft, setFilterDraft] = useState<Record<string, string[]>>({});
  const { showNotification, snackbar } = useNotificationSnackbar();
  const skipSearchDebounce = useRef(true);

  const { items: roleLookupItems } = useLookup(lookupService, LOOKUP_MASTERS.operatorRoles);

  const roleOptions = useMemo(
    () => lookupItemsToRoleOptionStrings(roleLookupItems),
    [roleLookupItems],
  );

  const roleFilterOptions = useMemo(
    () => lookupItemsToFilterOptions(roleLookupItems, (v) => v.trim()),
    [roleLookupItems],
  );

  const listFilterSections = useMemo(
    () => [
      { id: "status", label: "Status", options: USERS_STATUS_FILTER_OPTIONS },
      { id: "roleName", label: "Role", options: roleFilterOptions },
    ],
    [roleFilterOptions],
  );

  const appliedChipItems: ListAppliedFilterChipItem[] = useMemo(() => {
    const items: ListAppliedFilterChipItem[] = [];
    
    // Status filters
    for (const v of appliedListFilterStatus) {
      const option = USERS_STATUS_FILTER_OPTIONS.find((opt) => opt.value === v);
      items.push({
        id: `status:${v}`,
        label: option?.label || v,
        onRemove: () => {
          dispatch(setUsersListFilters({
            listFilterStatus: appliedListFilterStatus.filter(val => val !== v),
            listFilterRoleName: [...appliedListFilterRoleName],
          }));
          dispatch(fetchSettingsUsers());
        },
      });
    }
    
    // Role filters
    for (const v of appliedListFilterRoleName) {
      const option = roleFilterOptions.find((opt) => opt.value === v);
      items.push({
        id: `roleName:${v}`,
        label: option?.label || v,
        onRemove: () => {
          dispatch(setUsersListFilters({
            listFilterStatus: [...appliedListFilterStatus],
            listFilterRoleName: appliedListFilterRoleName.filter(val => val !== v),
          }));
          dispatch(fetchSettingsUsers());
        },
      });
    }
    
    return items;
  }, [appliedListFilterStatus, appliedListFilterRoleName, dispatch, roleFilterOptions]);

  const handleClearAllChips = useCallback(() => {
    dispatch(setUsersListFilters({
      listFilterStatus: [],
      listFilterRoleName: [],
    }));
    dispatch(fetchSettingsUsers());
  }, [dispatch]);

  const handleSearchTrigger = useCallback(
    (searchValue: string) => {
      setSearch(searchValue);
      dispatch(setUsersListSearch(searchValue));
      dispatch(fetchSettingsUsers());
    },
    [dispatch],
  );

  useEffect(() => {
    dispatch(fetchSettingsUsers());
  }, [dispatch]);

  const listPage = Math.floor(fromIndex / SETTINGS_USERS_LIST_PAGE_SIZE) + 1;

  const handlePageChange = useCallback(
    (nextPage: number) => {
      const from = (nextPage - 1) * SETTINGS_USERS_LIST_PAGE_SIZE;
      dispatch(
        setUsersListRange({
          fromIndex: from,
          toIndex: from + SETTINGS_USERS_LIST_PAGE_SIZE,
        }),
      );
      dispatch(fetchSettingsUsers());
    },
    [dispatch],
  );

  useEffect(() => {
    if (skipSearchDebounce.current) {
      skipSearchDebounce.current = false;
      return;
    }
    const id = window.setTimeout(() => {
      dispatch(setUsersListSearch(search));
      dispatch(fetchSettingsUsers());
    }, 300);
    return () => window.clearTimeout(id);
  }, [search, dispatch]);

  const nameSortDesc = sortingOrder === "desc";

  const filteredRows = useMemo(() => {
    return rows.filter((row) => {
      if (!includeDeactivated && row.deactivated) return false;
      return true;
    });
  }, [rows, includeDeactivated]);

  const deactivateTargetRows: UserRow[] = useMemo(() => {
    return filteredRows.filter(
      (r) =>
        selectedUserIds.includes(r.id) &&
        r.id !== SETTINGS_CURRENT_USER_ID &&
        (r.status === "active" || r.status === "inviteSent"),
    );
  }, [filteredRows, selectedUserIds]);

  const deactivateEnabled = useMemo(() => {
    const others = selectedUserIds.filter((id) => id !== SETTINGS_CURRENT_USER_ID);
    if (others.length === 0) return false;
    const byId = new Map(filteredRows.map((r) => [r.id, r]));
    return others.every((id) => {
      const row = byId.get(id);
      return row != null && (row.status === "active" || row.status === "inviteSent");
    });
  }, [filteredRows, selectedUserIds]);

  const handleNameHeaderClick = () => {
    const nextDesc = sortingOrder !== "desc";
    dispatch(
      setUsersListSort({
        sortingOrder: nextDesc ? "desc" : "asc",
        sortingField: "firstName",
      }),
    );
    dispatch(fetchSettingsUsers());
  };

  const openInvite = () => {
    dispatch(clearSettingsUserSubmitState());
    setDrawerMode("invite");
    setDrawerInitial(null);
    setDrawerOpen(true);
  };

  const openEdit = () => {
    if (selectedUserIds.length !== 1) return;
    const row = filteredRows.find((r) => r.id === selectedUserIds[0]);
    if (!row) return;
    dispatch(clearSettingsUserSubmitState());
    setDrawerMode("edit");
    setDrawerInitial({
      displayName: `${row.firstName} ${row.lastName}`.trim() || row.name,
      email: row.email,
      phone: row.phone,
      role: row.role,
      userId: Number(row.id),
    });
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
  };

  const openDeactivate = () => {
    if (deactivateTargetRows.length === 0) return;
    dispatch(clearSettingsUsersDeleteState());
    setDeactivateOpen(true);
  };

  const handleDeactivateClose = () => {
    setDeactivateOpen(false);
    dispatch(clearSettingsUsersDeleteState());
  };

  const handleDeactivateConfirm = useCallback(
    async ({ userIds }: { reason: string; userIds: number[] }) => {
      const count = userIds.length;
      try {
        await dispatch(deleteSettingsUsers({ userIds })).unwrap();
        const removed = new Set(userIds.map(String));
        setSelectedUserIds((prev) => prev.filter((id) => !removed.has(id)));
        setDeactivateOpen(false);
        dispatch(clearSettingsUsersDeleteState());
        showNotification({
          variant: "success",
          title: count === 1 ? "User deactivated" : "Users deactivated",
          description:
            count === 1
              ? "The selected user has been deactivated."
              : `${count} users have been deactivated.`,
        });
      } catch (e) {
        showNotification({
          variant: "error",
          title: "Deactivation failed",
          description: getThunkErrorMessage(e),
        });
      }
    },
    [dispatch, showNotification],
  );

  const handleDrawerSubmit = useCallback(
    async (p: UserFormSubmitPayload) => {
      try {
        if (drawerMode === "invite") {
          const body: CreateUserRequest = {
            firstName: p.firstName,
            ...(p.lastName !== undefined ? { lastName: p.lastName } : {}),
            emailId: p.emailId,
            roleName: p.roleName,
            contactNumber: p.contactNumber,
          };
          await dispatch(createSettingsUser(body)).unwrap();
          showNotification({
            variant: "success",
            title: "Invitation sent",
            description: "The user has been invited and will receive an email.",
          });
        } else {
          const userId = p.userId;
          if (userId == null) return;
          const body: UpdateUserRequest = {
            firstName: p.firstName,
            ...(p.lastName !== undefined ? { lastName: p.lastName } : {}),
            roleName: p.roleName,
            contactNumber: p.contactNumber,
          };
          await dispatch(updateSettingsUser({ userId, body })).unwrap();
          showNotification({
            variant: "success",
            title: "User updated",
            description: "Changes to this user have been saved.",
          });
        }
        setDrawerOpen(false);
      } catch (e) {
        showNotification({
          variant: "error",
          title: drawerMode === "invite" ? "Could not invite user" : "Could not update user",
          description: getThunkErrorMessage(e),
        });
      }
    },
    [dispatch, drawerMode, showNotification],
  );

  const editEnabled = selectedUserIds.length === 1;
  const hasSelection = selectedUserIds.length > 0;
  const resendInviteEnabled = useMemo(
    () => areSelectedUsersAllInviteSent(filteredRows, selectedUserIds),
    [filteredRows, selectedUserIds],
  );

  const reactivateEnabled = useMemo(
    () => areSelectedUsersAllDeactivated(filteredRows, selectedUserIds),
    [filteredRows, selectedUserIds],
  );

  const reactivateTargetRows: UserRow[] = useMemo(() => {
    return filteredRows.filter(
      (r) => selectedUserIds.includes(r.id) && r.status === "deactivated",
    );
  }, [filteredRows, selectedUserIds]);

  const resendInviteTargetRows: UserRow[] = useMemo(() => {
    return filteredRows.filter(
      (r) => selectedUserIds.includes(r.id) && r.status === "inviteSent",
    );
  }, [filteredRows, selectedUserIds]);

  const openReactivateConfirm = () => {
    if (reactivateTargetRows.length === 0) return;
    dispatch(clearSettingsUserSubmitState());
    setReactivateConfirmOpen(true);
  };

  const handleReactivateConfirmClose = () => {
    setReactivateConfirmOpen(false);
    dispatch(clearSettingsUserSubmitState());
  };

  const handleReactivateConfirm = useCallback(async () => {
    const ids = reactivateTargetRows.map((r) => Number(r.id)).filter((n) => Number.isFinite(n));
    if (ids.length === 0) return;
    const count = ids.length;
    try {
      await dispatch(reactivateSettingsUsers(ids)).unwrap();
      setSelectedUserIds([]);
      setReactivateConfirmOpen(false);
      dispatch(clearSettingsUserSubmitState());
      showNotification({
        variant: "success",
        title: count === 1 ? "User reactivated" : "Users reactivated",
        description:
          count === 1
            ? "The selected user can sign in again."
            : `${count} users have been reactivated.`,
      });
    } catch (e) {
      showNotification({
        variant: "error",
        title: "Reactivation failed",
        description: getThunkErrorMessage(e),
      });
    }
  }, [dispatch, reactivateTargetRows, showNotification]);

  const openResendInviteConfirm = () => {
    if (resendInviteTargetRows.length === 0) return;
    dispatch(clearSettingsUserSubmitState());
    setResendInviteConfirmOpen(true);
  };

  const handleResendInviteConfirmClose = () => {
    setResendInviteConfirmOpen(false);
    dispatch(clearSettingsUserSubmitState());
  };

  const handleResendInviteConfirm = useCallback(async () => {
    const ids = resendInviteTargetRows.map((r) => Number(r.id)).filter((n) => Number.isFinite(n));
    if (ids.length === 0) return;
    const count = ids.length;
    try {
      await dispatch(resendInviteSettingsUsers(ids)).unwrap();
      setSelectedUserIds([]);
      setResendInviteConfirmOpen(false);
      dispatch(clearSettingsUserSubmitState());
      showNotification({
        variant: "success",
        title: count === 1 ? "Invite resent" : "Invites resent",
        description:
          count === 1
            ? "A new invitation email has been sent."
            : `Invitation emails have been sent to ${count} users.`,
      });
    } catch (e) {
      showNotification({
        variant: "error",
        title: "Resend failed",
        description: getThunkErrorMessage(e),
      });
    }
  }, [dispatch, resendInviteTargetRows, showNotification]);

  const openFilterDrawer = useCallback(() => {
    setFilterDraft({
      status: [...appliedListFilterStatus],
      roleName: [...appliedListFilterRoleName],
    });
    setFilterDrawerOpen(true);
  }, [appliedListFilterStatus, appliedListFilterRoleName]);

  const handleFilterDrawerClose = useCallback(() => {
    setFilterDrawerOpen(false);
  }, []);

  const handleFilterApply = useCallback(
    (applied: Record<string, string[]>) => {
      dispatch(
        setUsersListFilters({
          listFilterStatus: applied.status ?? [],
          listFilterRoleName: applied.roleName ?? [],
        }),
      );
      dispatch(fetchSettingsUsers());
      setFilterDrawerOpen(false);
    },
    [dispatch],
  );

  return (
    <SettingsPageShell activeTab="users">
      <div className="users-page__main">
        <UsersToolbar
          includeDeactivated={includeDeactivated}
          onIncludeDeactivatedChange={setIncludeDeactivated}
          searchValue={search}
          onSearchTrigger={handleSearchTrigger}
          onFilterClick={openFilterDrawer}
          hasSelection={hasSelection}
          deactivateEnabled={deactivateEnabled}
          editEnabled={editEnabled}
          onInviteClick={openInvite}
          onEditClick={openEdit}
          onDeactivateClick={openDeactivate}
          reactivateEnabled={reactivateEnabled && !submitting}
          onReactivateClick={openReactivateConfirm}
          resendInviteEnabled={resendInviteEnabled && !submitting}
          onResendInviteClick={openResendInviteConfirm}
        />
        <ListAppliedFilterChips 
          items={appliedChipItems} 
          onClearAll={handleClearAllChips}
          totalResults={totalUsers}
        />
        <div className="users-page__table-scroll">
          <UsersTable
            loading={loading}
            rows={error ? [] : filteredRows}
            selectedUserIds={selectedUserIds}
            onSelectedUserIdsChange={setSelectedUserIds}
            currentUserId={SETTINGS_CURRENT_USER_ID}
            nameSortDescending={nameSortDesc}
            onNameHeaderClick={handleNameHeaderClick}
            paginationFooter={
              <ListTablePaginationBar
                totalCount={totalUsers}
                page={listPage}
                pageSize={SETTINGS_USERS_LIST_PAGE_SIZE}
                onPageChange={handlePageChange}
                disabled={loading}
              />
            }
          />
        </div>
      </div>
      <ListFiltersDrawer
        open={filterDrawerOpen}
        onClose={handleFilterDrawerClose}
        title="Filters"
        subtitle="Select filters for users."
        sections={listFilterSections}
        value={filterDraft}
        onChange={setFilterDraft}
        onApply={handleFilterApply}
      />
      <UserFormDrawer
        mode={drawerMode}
        open={drawerOpen}
        onClose={handleDrawerClose}
        initialValues={drawerInitial}
        onSubmit={handleDrawerSubmit}
        submitting={submitting}
        submitError={submitError}
        roleOptions={roleOptions}
      />
      <DeactivateUsersDialog
        open={deactivateOpen}
        onClose={handleDeactivateClose}
        selectedUsers={deactivateTargetRows}
        onConfirm={handleDeactivateConfirm}
        submitting={deleteSubmitting}
        errorText={deleteError}
      />
      <ConfirmActionDialog
        open={reactivateConfirmOpen}
        onClose={handleReactivateConfirmClose}
        title="Reactivate user"
        description={buildReactivateUsersDescription(reactivateTargetRows)}
        primaryLabel="Reactivate"
        onPrimaryClick={() => {
          void handleReactivateConfirm();
        }}
        primaryDisabled={reactivateTargetRows.length === 0}
        primaryLoading={submitting}
        onSecondaryClick={handleReactivateConfirmClose}
        errorText={submitError}
      />
      <ConfirmActionDialog
        open={resendInviteConfirmOpen}
        onClose={handleResendInviteConfirmClose}
        title="Resend invite"
        description={buildResendInviteUsersDescription(resendInviteTargetRows)}
        primaryLabel="Resend invite"
        onPrimaryClick={() => {
          void handleResendInviteConfirm();
        }}
        primaryDisabled={resendInviteTargetRows.length === 0}
        primaryLoading={submitting}
        onSecondaryClick={handleResendInviteConfirmClose}
        errorText={submitError}
      />
      {snackbar}
    </SettingsPageShell>
  );
};
